import React, { useState } from "react";
import { toast } from "sonner";
import { Target, Mail, Loader2, Copy } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function JobCardActions({ job }) {
  const [matching, setMatching] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const checkMatch = async () => {
    setMatching(true);
    try {
      const { data } = await api.post(`/jobs/me/match/${job.id}`);
      setMatchResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save your resume first on this page");
    } finally {
      setMatching(false);
    }
  };

  const draftEmail = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/jobs/me/draft-email", { job_id: job.id });
      setEmailDraft(data);
      setEmailOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save your resume first on this page");
    } finally {
      setDrafting(false);
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(`Subject: ${emailDraft.subject}\n\n${emailDraft.body}`);
    toast.success("Email copied to clipboard");
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`job-card-actions-${job.id}`}>
      <div className="flex items-center gap-3">
        <button onClick={checkMatch} disabled={matching} data-testid={`job-check-match-btn-${job.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-emerald-300 hover:text-emerald-200 disabled:opacity-60">
          {matching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Target className="w-3.5 h-3.5" />} Check My Match
        </button>
        <button onClick={draftEmail} disabled={drafting} data-testid={`job-draft-email-btn-${job.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200 disabled:opacity-60">
          {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Draft Email
        </button>
      </div>

      {matchResult && (
        <div className="mt-3 bg-white/[0.03] rounded-lg p-3" data-testid={`job-match-result-${job.id}`}>
          <p className="text-xs font-bold text-emerald-400">{matchResult.match_score}% match</p>
          <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{matchResult.cover_note}</p>
        </div>
      )}

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg" data-testid={`job-email-dialog-${job.id}`}>
          <DialogHeader>
            <DialogTitle className="text-white">Draft Application Email</DialogTitle>
            <DialogDescription>AI-generated email draft for this role, based on your saved resume. Review before sending.</DialogDescription>
          </DialogHeader>
          {emailDraft && (
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold text-slate-400 mb-1">Subject</p>
                <p className="text-sm text-white bg-white/5 rounded-lg px-3 py-2" data-testid="job-email-subject">{emailDraft.subject}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 mb-1">Body</p>
                <pre className="text-xs text-slate-300 whitespace-pre-wrap bg-white/5 rounded-lg p-3 max-h-64 overflow-y-auto" data-testid="job-email-body">{emailDraft.body}</pre>
              </div>
              <button onClick={copyEmail} data-testid="job-email-copy-button" className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
                <Copy className="w-4 h-4" /> Copy Email
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
